/**
 * Build a curated composition from filled slots (fillSlots) + the background
 * detail, in the layout's authored coordinate space — so symbols and text land
 * exactly where the layout/background defines them.
 *
 * Coordinate space (matches tapgen/src/generate/Tapestry.js + lo_slots.js):
 *   viewBox = WINDOW_WIDTH × (WINDOW_WIDTH * heightRatio), WINDOW_WIDTH = 1800.
 *   symbol:  x = slot.x * width,  y = slot.y * height,  size = maxSize * height.
 *   text:    each slot.textpath entry is a guide path in that same space.
 *
 * Symbols are returned in the renderer's height-normalized units (x ∈ [0,
 * bgAspect], y ∈ [0,1], size in height units) for TapestrySymbol; text is
 * returned with its authored guide geometry for CuratedText (which renders an
 * SVG with the viewBox below, so the authored coords are used verbatim).
 */

import { parseGuides, resolveFontSize } from './curatedText.js';

const WINDOW_WIDTH = 1800;
// Fallback when a background has no heightRatio: 16/9 canvas aspect.
const DEFAULT_HEIGHT_RATIO = 9 / 16;

// Background `font` key → font family + size scale, mirroring tapgen
// fonts/fonts.js. Fira renders larger than the hand font at the same px, so it
// is scaled down to match. The host must make these families available.
// `kalam` / `patrick` are the graphic-recording hands added for generative
// scenes (TT-1046); hosts self-host them like Hand1 (tapfly: globals.css).
export const FONTS = {
  hand: { family: 'Hand1-Regular', sizeScale: 1 },
  fira: { family: 'Fira Sans', sizeScale: 0.75 },
  kalam: { family: 'Kalam', sizeScale: 0.92 },
  patrick: { family: 'Patrick Hand', sizeScale: 0.95 },
};
const DEFAULT_FONT_KEY = 'hand';

function resolveFont(key) {
  return FONTS[key] || FONTS[DEFAULT_FONT_KEY];
}

// Resolve a slot's text color from the background color data, matching the
// static renderer: textpathData.color keys into bg colors, defaulting to
// "primary". Colors apply to text only (symbol art is left untouched).
function textColor(colors, textpathData) {
  const key = (textpathData && textpathData.color) || 'primary';
  return (colors && colors[key]) || '#000000';
}

// Subtle black drop shadow added behind white text so it stays legible over
// light or busy regions of a background (white alone can wash out).
const WHITE_TEXT_SHADOW = 'rgba(0,0,0,0.55)';

function isWhiteColor(c) {
  if (!c) return false;
  const v = String(c).trim().toLowerCase();
  return v === '#fff' || v === '#ffffff' || v === 'white' || v === 'rgb(255,255,255)' || v === 'rgba(255,255,255,1)';
}

/**
 * @param {{slots: Array, title: object|null}} filled  output of fillSlots
 * @param {object} state   reducer state (for symbol sentence buffers)
 * @param {object} detail  normalized background detail ({ colors, heightRatio })
 * @returns {{ viewBox: {width:number,height:number}, bgAspect: number,
 *            symbols: Array, texts: Array, title: object|null }}
 */
export function buildCuratedComposition(filled, state, detail) {
  const symbolsMap = (state && state.symbols) || {};
  const colors = (detail && detail.colors) || {};
  const heightRatio = (detail && detail.heightRatio) || DEFAULT_HEIGHT_RATIO;
  const bgAspect = 1 / heightRatio;
  const width = WINDOW_WIDTH;
  const height = WINDOW_WIDTH * heightRatio;
  // Font specified by the background (e.g. "fira" → Fira Sans); fira is scaled
  // to match the hand font's visual size.
  const font = resolveFont(detail && detail.font);

  const symbols = [];
  const texts = [];
  // A text's id keys its React element AND its guide-path DOM ids in CuratedText
  // (`cg-${id}-${gi}`, stored in a pathRefs Map). If a layout authors two slots
  // with the same id, those collide — duplicate React keys plus one slot's text
  // riding the other's guide. Disambiguate here so the renderer always gets
  // unique ids regardless of layout-data quality.
  const idCounts = new Map();
  const uniqueTextId = (rawId) => {
    const base = rawId != null ? String(rawId) : 'text';
    const seen = idCounts.get(base) || 0;
    idCounts.set(base, seen + 1);
    return seen === 0 ? base : `${base}-${seen}`;
  };

  for (const slot of (filled && filled.slots) || []) {
    if (slot.type === 'symbol' && slot.symbolId) {
      const entry = symbolsMap[slot.symbolId] || { sentences: [] };
      symbols.push({
        symbolId: slot.symbolId,
        role: 'curated',
        entry,
        x: (slot.x ?? 0.5) * bgAspect, // slot.x is a fraction of width
        y: slot.y ?? 0.5,
        size: slot.maxSize || 0.15,
        side: 1,
        styles: slot.symbolStyles || null, // preferred symbol-image styles

        // TapestrySymbol/External read these; curated symbols have no generated
        // external curve (their text is rendered by CuratedText instead).
        curve: null,
        layout: null,
      });
    }

    // Any slot (symbol or phrase) with a chosen phrase and authored guides.
    if (slot.phrase && slot.textpath) {
      const guides = parseGuides(slot.textpath);
      if (guides.length) {
        const td = slot.textpathData || {};
        // Array textpaths are aggregate (one guide per line). A single-string
        // textpath is one guide reused over `lines` stacked lines.
        const isArray = Array.isArray(slot.textpath);
        const color = textColor(colors, td);
        texts.push({
          id: uniqueTextId(slot.id),
          guides,
          maxLines: isArray ? guides.length : Number(td.lines) || 1,
          single: !isArray,
          phrase: slot.phrase,
          phraseFallbacks: slot.phraseFallbacks || [],
          fontSize: resolveFontSize(td.font_size) * font.sizeScale,
          fontFamily: font.family,
          color,
          dropShadow: td.drop_shadow || (isWhiteColor(color) ? WHITE_TEXT_SHADOW : null),
          uppercase: !!td.uppercase,
        });
      }
    }
  }

  return {
    viewBox: { width, height },
    bgAspect,
    symbols,
    texts,
    fontFamily: font.family,
    title: filled && filled.title,
  };
}
